<template>
  <div>
    <div class="mb-4 flex justify-between items-center">
      <h2 class="text-lg font-semibold text-gray-900">Patient CCLF Part B Records</h2>
      <ExportMenu
        :disabled="!rowData.length"
        :exports="[{ type: 'excel', action: exportToExcel }]"
      />
    </div>

    <div style="height: 500px;">
      <AgGridVue
        :theme="compactListTheme"
        style="width: 100%; height:100%"
        :column-defs="columnDefs"
        :row-data="rowData"
        :default-col-def="defaultColDef"
        :grid-options="gridOptions"
        :loading="loading"
        @grid-ready="onGridReady"
      />

    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import {compactListTheme} from '@/utils/common';
import CC_ToolTip from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_ToolTip.vue";
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';

export default defineComponent({
  name: 'Detail_Patient_CCLF_PartB_Table',
  computed: {
    compactListTheme() {
      return compactListTheme
    },
  },
  components: {
    AgGridVue,
    ExportMenu,
  },
  props: {
    patientIdentifier: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      rowData: [],
      gridApi: null,
      gridOptions: {
        suppressAggFuncInHeader: true,
        domLayout: this.autoHeight ? 'autoHeight' : null,
        treeData: true,
        animateRows: true,
        groupDefaultExpanded: -1, // Expand all groups by default
        treeDataChildrenField:'lines',
        getRowClass: (params) => {
          return params.node.group ? 'bg-gray-100 font-semibold' : '';
        },
        autoGroupColumnDef: {
          field: 'cur_clm_uniq_id',
          headerName: 'Claim ID / Line Number',
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
            suppressCount:true,
            innerRenderer: (params) => {
              if (params.node.group) {
                // Parent row - show claim ID
                return `Claim: ${params.data.cur_clm_uniq_id}`;
              } else {
                // Child row - show line number
                return `Line ${params.data.clm_line_num}`;
              }
            }
          }
        }
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },

      columnDefs: [
        {
          field:'clm_thru_dt',
          sort:'desc',
          hide:true
        },
        {
          field: 'clm_line_from_dt',
          headerName: 'Line From Date',
          valueFormatter: this.dateFormatter,
          valueGetter: (params) => {
            return params.node.group ? params.data.clm_from_dt : params.data.clm_line_from_dt;
          },
        },
        {
          field: 'clm_line_thru_dt',
          headerName: 'Line Through Date',
          valueFormatter: this.dateFormatter,
          valueGetter: (params) => {
            return params.node.group ? params.data.clm_thru_dt : params.data.clm_line_thru_dt;
          },
        },
        {
          field: 'clm_line_hcpcs_cd',
          headerName: 'HCPCS Code'
        },
        {
          field: 'clm_rndrg_prvdr_tax_num',
          headerName: 'Rendering Provider Tax Number'
        },
        {
          field: 'rndrg_prvdr_npi_num',
          headerName: 'Rendering Provider NPI'
        },
        {
          field: 'npi_last_name',
          headerName: 'NPI Last Name',
          cellClass: 'sensitive',
        },
        {
          field:'npi_first_name',
          headerName: 'NPI First Name',
          cellClass: 'sensitive',
        },
        {
          field: 'clm_efctv_dt',
          headerName: 'Effective Date',
          valueFormatter: this.dateFormatter
        },
        {
          field: 'clm_dgns_cds',
          cellClass:'font-normal',
          cellRenderer: CC_ToolTip,
          cellRendererParams: (params) => {
            const diagnosisCodes = params.value || [];
            const filteredCodes = diagnosisCodes.filter(code => code && code.trim() !== '');
            const formattedText = filteredCodes
              .map((code, index) => `<strong>${index + 1}.</strong> ${code}`)
              .join('<br>');

            return {
              displayText: diagnosisCodes.filter(code => code && code.trim() !== '').join(', '),              tooltipContent: formattedText,
              hide: !params.node.group
            };
          }
        },
        {
          field: 'clm_pmt_amt',
          headerName: 'Allowed Chg',
          valueGetter: (params) => {
            return params.node.group ? params.data.clm_pmt_amt : params.data.clm_line_cvrd_pd_amt;
          },
        },
      ]
    };
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await axios.post('api/returnpatientdata/', {
          data_type: 'cclf_partb',
          patient_identifier: this.patientIdentifier,
        });

        // Transform the data to create tree structure
        this.rowData = this.transformToTreeData(response.data || []);
      } catch (error) {
        console.error('Error fetching tree data:', error);
        this.rowData = [];
      } finally {
        this.loading = false;
      }
    },
    transformToTreeData(data) {
      // Group data by cur_clm_uniq_id
      const groupedData = {};

      data.forEach(record => {
        const claimId = record.cur_clm_uniq_id;

        if (!groupedData[claimId]) {
          groupedData[claimId] = {
            id: claimId,
            cur_clm_uniq_id: claimId,
            // Include parent-level claim data
            clm_from_dt: record.clm_from_dt,
            clm_thru_dt: record.clm_thru_dt,
            clm_type_cd: record.clm_type_cd,
            clm_dgns_cds: [
              record.clm_dgns_1_cd,
              record.clm_dgns_2_cd,
              record.clm_dgns_3_cd,
              record.clm_dgns_4_cd,
              record.clm_dgns_5_cd,
              record.clm_dgns_6_cd,
              record.clm_dgns_7_cd,
              record.clm_dgns_8_cd,
              record.clm_dgns_9_cd,
              record.clm_dgns_10_cd,
              record.clm_dgns_11_cd,
              record.clm_dgns_12_cd,
            ],
            lines: []
          };
        }

        // Add line item as child
        groupedData[claimId].lines.push({
          clm_line_num: record.clm_line_num,
          clm_line_from_dt: record.clm_line_from_dt,
          clm_line_thru_dt: record.clm_line_thru_dt,
          clm_line_hcpcs_cd: record.clm_line_hcpcs_cd,
          clm_rndrg_prvdr_tax_num: record.clm_rndrg_prvdr_tax_num,
          rndrg_prvdr_npi_num: record.rndrg_prvdr_npi_num,
          clm_efctv_dt: record.clm_efctv_dt,
          npi_last_name: record.npi_last_name,
          npi_first_name: record.npi_first_name,
          ...record
        });
      });

      // Convert grouped object to array
      return Object.values(groupedData);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', this.onWindowResize);

    },

    exportToExcel() {
      if (this.gridApi) {
        this.gridApi.exportDataAsExcel({
          fileName: `patient_attribution_${this.patientIdentifier}_${new Date().toISOString().split('T')[0]}.xlsx`,
          sheetName: 'Attribution'
        });
      }
    },
    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },
    dateFormatter(params) {
      if (!params.value) return '';
      const date = new Date(params.value + 'T00:00:00');
      if (isNaN(date)) return params.value;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  },
  mounted() {
    this.fetchData();
  }
});
</script>

<style scoped>

</style>
